import { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { leadsAPI, authAPI, settingsAPI, festAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { validateName, validatePhone } from '@/lib/validation';
import { alsText, toTitleCase, initial, schrittText } from '@/lib/text';
import {
  Plus, Search, Upload, Download, Filter, MoreHorizontal,
  Phone, Mail, ChevronDown, ChevronLeft, ChevronRight, X, User, Archive, MessageCircle, SlidersHorizontal, Users, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import ImportDialog from '@/components/ImportDialog';
import { toast } from 'sonner';

// Utility function to convert to title case

/* Naechster Schritt in der Liste (Launch 23.09.2026): was, faellig seit/bis; rot wenn ueberfaellig */
function NaechsterSchritt({ v }) {
  if (!v || (typeof v === 'object' && !v.was)) return <span className="text-xs text-muted-foreground">—</span>;
  if (typeof v !== 'object') return <span className="text-xs truncate block">{String(v)}</span>;
  const d = v.faellig_am ? new Date(v.faellig_am) : null;
  const tage = d && !Number.isNaN(d.getTime()) ? Math.floor((Date.now() - d.getTime()) / 86400000) : null;
  const frist = tage == null ? '' : tage > 0 ? `seit ${tage} Tag${tage === 1 ? '' : 'en'}` : tage === 0 ? 'heute' : `in ${-tage} Tag${tage === -1 ? '' : 'en'}`;
  return (
    <div className="min-w-0">
      <p className="text-xs truncate">{v.was}</p>
      {frist && <p className={`text-[10px] ${tage > 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>{tage > 0 ? 'überfällig ' : 'fällig '}{frist}</p>}
    </div>
  );
}

export default function LeadsPage() {
  const [mehrFilter, setMehrFilter] = useState(false);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [quelleFilter, setQuelleFilter] = useState('');   // herkunft.quelle = utm_source
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [agents, setAgents] = useState([]);
  const { user } = useAuth();
  const { settings, stageMap, formatCurrency } = useSettings();
  const navigate = useNavigate();
  const SOURCES = settings.lead_sources || [];
  const stages = settings.pipeline_stages || [];
  const bereiche = settings.bereiche || [];
  const altersgruppen = settings.altersgruppen || [];
  const kampagnenList = settings.kampagnen || [];
  const [bereichFilter, setBereichFilter] = useState('');
  const [altersgruppeFilter, setAltersgruppeFilter] = useState('');
  const [kampagneFilter, setKampagneFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [noShowFilter, setNoShowFilter] = useState(false);
  const [abschlussFilter, setAbschlussFilter] = useState('');
  const [terminStatusFilter, setTerminStatusFilter] = useState('');
  // '' = nur aktive (Backend-Standard), 'archiviert' = Archiv, 'alle' = beides
  const [archivFilter, setArchivFilter] = useState('');
  // Founding "Small Group" vom Sommerfest: die zwoelf Plaetze bzw. die
  // Warteliste als eigene Ansicht — Cagla arbeitet die Liste am Montag ab.
  const [foundingFilter, setFoundingFilter] = useState('');
  const [showArchivAlte, setShowArchivAlte] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter aus URL übernehmen (z. B. wenn man von einer Dashboard-Kachel kommt)
  useEffect(() => {
    if (searchParams.get('bereich')) setBereichFilter(searchParams.get('bereich'));
    if (searchParams.get('stage')) setStageFilter(searchParams.get('stage'));
    if (searchParams.get('source')) setSourceFilter(searchParams.get('source'));
    if (searchParams.get('quelle')) setQuelleFilter(searchParams.get('quelle'));
    if (searchParams.get('kampagne')) setKampagneFilter(searchParams.get('kampagne'));
    if (searchParams.get('altersgruppe')) setAltersgruppeFilter(searchParams.get('altersgruppe'));
    if (searchParams.get('abschluss_status')) setAbschlussFilter(searchParams.get('abschluss_status'));
    if (searchParams.get('termin_status')) setTerminStatusFilter(searchParams.get('termin_status'));
    if (searchParams.get('no_show') === 'true') setNoShowFilter(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearExtraFilters = () => {
    setNoShowFilter(false); setAbschlussFilter(''); setTerminStatusFilter('');
    setBereichFilter(''); setAltersgruppeFilter('');
    if (searchParams.toString()) setSearchParams({});
  };
  const hasExtraFilter = noShowFilter || abschlussFilter || terminStatusFilter;

  const fetchLeads = useCallback(async (searchOverride) => {
    try {
      const params = {
        skip: (page - 1) * limit,
        limit: limit
      };
      const q = searchOverride !== undefined ? searchOverride : search;
      if (q) params.search = q;
      if (stageFilter) params.stage = stageFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (quelleFilter) params.quelle = quelleFilter;
      if (bereichFilter) params.bereich = bereichFilter;
      if (altersgruppeFilter) params.altersgruppe = altersgruppeFilter;
      if (kampagneFilter) params.kampagne = kampagneFilter;
      if (assignedFilter) params.assigned_to = assignedFilter;
      if (noShowFilter) params.no_show = true;
      if (abschlussFilter) params.abschluss_status = abschlussFilter;
      if (terminStatusFilter) params.termin_status = terminStatusFilter;
      if (archivFilter) params.archiv = archivFilter;
      if (foundingFilter) params.founding = foundingFilter;
      const res = await leadsAPI.list(params);
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch (err) {

    } finally {
      setLoading(false);
    }
  }, [search, stageFilter, sourceFilter, quelleFilter, bereichFilter, altersgruppeFilter, kampagneFilter, assignedFilter, noShowFilter, abschlussFilter, terminStatusFilter, archivFilter, foundingFilter, page, limit]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await authAPI.users();
      setAgents(res.data || []);
    } catch (err) {
      
    }
  }, []);

  // Debounce search input
  const searchTimer = useRef(null);
  const handleSearch = useCallback((value) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchLeads(value), 350);
  }, [fetchLeads]);
  useEffect(() => () => clearTimeout(searchTimer.current), []);

  // Fetch on filter/page changes (fetchLeads identity changes via useCallback deps)
  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Fetch agents for assignment dropdown
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      fetchAgents();
    }
  }, [user, fetchAgents]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  const handleDelete = async (id) => {
    try {
      await leadsAPI.delete(id);
      toast.success('Mitglied gelöscht');
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Loeschen fehlgeschlagen');
    }
  };

  // Die Liste kommt ueber den API-Client, damit das Token mitgeht — ein
  // window.open() waere ohne Anmeldung unterwegs und liefe in 401.
  const foundingCSV = async () => {
    try {
      const res = await festAPI.foundingCSV();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'founding-small-group.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Liste konnte nicht geladen werden');
    }
  };

  const handleArchiv = async (lead) => {
    try {
      const res = await leadsAPI.archiv(lead.id, !lead.archiviert);
      if (res.data.archiviert) {
        const zu = res.data.wiedervorlagen_geschlossen;
        toast.success('Archiviert' + (zu ? ` — ${zu} offene Wiedervorlage(n) geschlossen` : ''));
      } else {
        toast.success('Aus dem Archiv geholt');
      }
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archivieren fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-5 fade-in" data-testid="leads-page">
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Leads</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">{total} Kontakte · Mitglieder stehen unter Mitgliedschaften</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const res = await leadsAPI.exportCSV({ include_memberships: true });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a');
              a.href = url;
              a.download = 'mitglieder_export.csv';
              a.click();
              window.URL.revokeObjectURL(url);
              toast.success('Export heruntergeladen');
            } catch { toast.error('Export fehlgeschlagen'); }
          }} data-testid="export-leads-btn" className="gap-1.5 h-8 sm:h-9 text-xs sm:text-sm hidden sm:flex">
            <Download className="w-3.5 h-3.5" /> Exportieren
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-leads-btn" className="gap-1.5 h-8 sm:h-9 text-xs sm:text-sm hidden sm:flex">
            <Upload className="w-3.5 h-3.5" /> Importieren
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-lead-btn" className="gap-1.5 h-8 sm:h-9 text-xs sm:text-sm shadow-sm">
            <Plus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Neues Mitglied</span><span className="sm:hidden">Neu</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
        <div className="relative flex-1 min-w-[150px] sm:min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Name, E-Mail, Telefon oder Mitgliedsnummer..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="lead-search-input"
          />
        </div>
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="stage-filter" className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="all">Alle Stufen</option>
          {stages.map(s => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
        {agents.length > 0 && (
          <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="assigned-filter" className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
            <option value="all">Alle Mitarbeiter</option>
            {agents.map(a => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
          </select>
        )}
        <button type="button" onClick={() => setMehrFilter(m => !m)} data-testid="mehr-filter"
                className={`flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm ${mehrFilter || sourceFilter || bereichFilter || altersgruppeFilter || kampagneFilter || archivFilter || foundingFilter ? 'border-primary/40 bg-primary/5' : 'border-input bg-background'}`}>
          <SlidersHorizontal className="w-3.5 h-3.5" /> {mehrFilter ? 'weniger Filter' : 'mehr Filter'}
        </button>
        {mehrFilter && (<>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="source-filter" className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="all">Alle Quellen</option>
          {SOURCES.map(s => (
            <option key={s} value={s}>{toTitleCase(s)}</option>
          ))}
        </select>
        {quelleFilter && (
          <button type="button" onClick={() => setQuelleFilter('')} data-testid="quelle-filter"
                  className="flex h-9 items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-3 text-sm" title="Herkunft (utm_source) — klicken zum Entfernen">
            Herkunft: {quelleFilter} ✕
          </button>
        )}
        <select value={bereichFilter} onChange={(e) => setBereichFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="bereich-filter" className="flex h-9 w-[150px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="all">Alle Bereiche</option>
          {bereiche.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
        <select value={altersgruppeFilter} onChange={(e) => setAltersgruppeFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="altersgruppe-filter" className="flex h-9 w-[150px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="all">Alle Altersgruppen</option>
          {altersgruppen.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
        <select value={kampagneFilter} onChange={(e) => setKampagneFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="kampagne-filter" className="flex h-9 w-[150px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="all">Alle Kampagnen</option>
          {kampagnenList.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
          {kampagneFilter && !kampagnenList.some(k => k.key === kampagneFilter) && <option value={kampagneFilter}>{kampagneFilter}</option>}
        </select>
        <select value={archivFilter} onChange={(e) => { setArchivFilter(e.target.value); setPage(1); }} data-testid="archiv-filter" className="flex h-9 w-[140px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="">Aktive</option>
          <option value="archiviert">Archiv</option>
          <option value="alle">Aktive + Archiv</option>
        </select>
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <Button variant="outline" size="sm" onClick={() => setShowArchivAlte(true)} data-testid="archiv-alte-btn" className="gap-1.5 h-9 text-xs sm:text-sm">
            <Archive className="w-3.5 h-3.5" /> Alte archivieren
          </Button>
        )}
        <select value={foundingFilter} onChange={(e) => { setFoundingFilter(e.target.value); setPage(1); }} data-testid="founding-filter" className="flex h-9 w-[200px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="">Kein Founding-Filter</option>
          <option value="small_group">Founding Small Group</option>
          <option value="warteliste">Founding Warteliste</option>
        </select>
        {foundingFilter === 'small_group' && (
          <Button variant="outline" size="sm" data-testid="founding-csv"
                  onClick={foundingCSV}
                  className="gap-1.5 h-9 text-xs sm:text-sm">
            <Download className="w-3.5 h-3.5" /> Liste als CSV
          </Button>
        )}
        </>)}
      </div>

      {/* Aktiver Sonderfilter (z. B. von einer Dashboard-Kachel) */}
      {hasExtraFilter && (
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium">
            Filter aktiv: {noShowFilter ? 'Nicht erschienen' : abschlussFilter ? 'Abschlüsse' : terminStatusFilter === 'geplant' ? 'Probetraining geplant' : terminStatusFilter === 'besucht' ? 'Probetraining besucht' : terminStatusFilter}
          </span>
          <button onClick={clearExtraFilters} className="text-muted-foreground hover:text-foreground underline" data-testid="clear-extra-filter">
            Zurücksetzen
          </button>
        </div>
      )}

      {/* Table */}
      <Card className="border-border/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground">Name</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground hidden sm:table-cell">Kontakt</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground hidden xl:table-cell">Bereich</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground">Stufe</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground hidden md:table-cell">Nächster Schritt</th>
                <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground hidden lg:table-cell">Zuständig</th>
                <th className="text-right px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-muted-foreground sticky right-0 bg-muted">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  className="border-b border-border/40 hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => navigate(`/leads/${lead.id}`, { 
                    state: { 
                      from: '/leads',
                      leadIds: leads.map(l => l.id)
                    } 
                  })}
                  data-testid={`lead-row-${lead.id}`}
                >
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center text-[10px] sm:text-xs font-semibold text-primary flex-shrink-0">
                        {initial(lead.name)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-xs sm:text-sm truncate">{alsText(lead.name)}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground capitalize truncate sm:hidden">{alsText(lead.phone) || alsText(lead.email)}</p>
                        <p className="text-[10px] text-muted-foreground hidden sm:block">{toTitleCase(lead.source)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 hidden sm:table-cell">
                    <div className="space-y-0.5">
                      {lead.email && <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{alsText(lead.email)}</p>}
                      {lead.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{alsText(lead.phone)}</p>}
                    </div>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 hidden xl:table-cell">
                    {lead.bereich && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted font-medium">
                        {bereiche.find(b => b.key === lead.bereich)?.label || alsText(lead.bereich)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3">
                    <Badge variant="secondary" className="text-[10px] px-1.5 sm:px-2 py-0.5 text-white" style={{ backgroundColor: stageMap[lead.stage]?.color || '#6B7280' }}>
                      {stageMap[lead.stage]?.label || toTitleCase(lead.stage)}
                    </Badge>
                    {lead.archiviert && (
                      <Badge variant="outline" className="ml-1 text-[10px] px-1.5 py-0.5 text-muted-foreground">
                        Archiviert
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 hidden md:table-cell max-w-[220px]">
                    <NaechsterSchritt v={lead.naechster_schritt} />
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 hidden lg:table-cell">
                    <span className="text-xs">{alsText(lead.assigned_to_name) || '—'}</span>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-right sticky right-0 bg-background whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <span className="inline-flex items-center gap-0.5 mr-1 align-middle">
                      {lead.phone && <a href={`https://wa.me/${String(lead.phone).replace(/\D/g, '').replace(/^0/, '49')}`} target="_blank" rel="noreferrer" title="WhatsApp öffnen" className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50" data-testid={`wa-${lead.id}`}><MessageCircle className="w-4 h-4" /></a>}
                      {lead.phone && <a href={`tel:${lead.phone}`} title="Anrufen" className="p-1.5 rounded-md text-muted-foreground hover:bg-muted" data-testid={`tel-${lead.id}`}><Phone className="w-4 h-4" /></a>}
                    </span>
                    <ActionsMenu
                      leadId={lead.id}
                      onView={() => navigate(`/leads/${lead.id}`, { 
                        state: { 
                          from: '/leads',
                          leadIds: leads.map(l => l.id)
                        } 
                      })}
                      onDelete={(user?.role === 'admin' || user?.role === 'manager') ? () => handleDelete(lead.id) : null}
                      archiviert={!!lead.archiviert}
                      onArchiv={() => handleArchiv(lead)}
                    />
                  </td>
                </tr>
              ))}
              {leads.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-muted-foreground">
                    Keine Mitglieder gefunden. Erstelle dein erstes Mitglied.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            Zeige {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} von {total} Mitgliedern
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">Seite {page} von {Math.ceil(total / limit)}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(total / limit)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add Lead Dialog */}
      <AddLeadDialog open={showAdd} onClose={() => setShowAdd(false)} onSuccess={fetchLeads} agents={agents} />

      {/* Import Dialog */}
      <ImportDialog open={showImport} onClose={() => setShowImport(false)} onSuccess={fetchLeads} />

      {/* Alte Leads archivieren (Massenaktion mit Vorschau) */}
      <ArchivAlteDialog open={showArchivAlte} onClose={() => setShowArchivAlte(false)} onSuccess={fetchLeads} />
    </div>
  );
}


function AddLeadDialog({ open, onClose, onSuccess, agents }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', source: 'laufkundschaft', kanal: 'laufkundschaft',
    notes: '', stage: 'neuer_lead', assigned_to: '',
    bereich: '', altersgruppe: '', kampagne: '',
    naechster_schritt: '', termin_status: '', priority: 'mittel',
    custom_fields: {},
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  /* Familie (Betreiber 26.10.2026: „mehrere Kinder mit derselben Nummer der Eltern anlegen“): Elternteil oben (Name,
     Nummer, E-Mail), darunter die Kinder. Jedes Kind wird ein eigener Lead mit der Nummer der Eltern, das Elternteil
     das Elternkonto, das alle Kinder verwaltet (POST /api/familie/anlegen). */
  const leerKind = () => ({ vorname: '', nachname: '', geburtsdatum: '' });
  const [familie, setFamilie] = useState(false);
  const [kinder, setKinder] = useState([leerKind(), leerKind()]);
  const kindSetzen = (i, k, v) => setKinder(ks => ks.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const { settings } = useSettings();
  const stages = settings.pipeline_stages || [];
  const SOURCES = settings.lead_sources || [];
  const bereiche = settings.bereiche || [];
  const altersgruppen = settings.altersgruppen || [];
  const kampagnenList = settings.kampagnen || [];

  useEffect(() => {
    if (open) {
      setCustomFieldDefs(settings.custom_fields_leads || []);
      setErrors({});
    }
  }, [open, settings]);

  const validate = () => {
    const e = {};
    const nameErr = validateName(form.name, 'Name', { min: 2, max: 100 });
    if (nameErr) e.name = nameErr;
    if (form.email && !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(form.email.trim())) e.email = 'Ungültiges E-Mail-Format';
    if (form.phone) {
      const phoneErr = validatePhone(form.phone);
      if (phoneErr) e.phone = phoneErr;
    }
    if (form.notes && form.notes.length > 2000) e.notes = 'Maximal 2000 Zeichen';
    if (familie) {
      if (!form.phone) e.phone = 'Handynummer der Eltern angeben';
      const namen = kinder.map(k => k.vorname.trim().toLowerCase()).filter(Boolean);
      if (namen.length === 0) e.kinder = 'Mindestens ein Kind mit Vornamen';
      else if (kinder.some(k => !k.vorname.trim() && (k.nachname || k.geburtsdatum))) e.kinder = 'Jedes Kind braucht einen Vornamen';
      else if (new Set(namen).size !== namen.length) e.kinder = 'Zwei Kinder mit demselben Vornamen';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    if (familie) {
      try {
        const r = await leadsAPI.familie({
          eltern: { name: form.name.trim(), telefon: form.phone.replace(/[\s\-\(\)]/g, ''), email: form.email?.trim().toLowerCase() || null },
          kinder: kinder.filter(k => k.vorname.trim()).map(k => ({ vorname: k.vorname.trim(), nachname: k.nachname.trim(), geburtsdatum: k.geburtsdatum || null })),
          source: form.source, herkunft: { kanal: form.kanal }, kampagne: form.kampagne || null, stage: form.stage, priority: form.priority,
          termin_status: form.termin_status || null, naechster_schritt: form.naechster_schritt || null, notes: form.notes || null,
          assigned_to: form.assigned_to || null,
        });
        const n = r.data.kinder.length;
        toast.success(`Familie angelegt: ${n} ${n === 1 ? 'Kind' : 'Kinder'}${r.data.eltern.neu ? '' : ' beim vorhandenen Elternkonto'}`
          + (r.data.schon_da.length ? ` · schon da: ${r.data.schon_da.map(k => k.name).join(', ')}` : ''));
        onSuccess(); onClose();
        setKinder([leerKind(), leerKind()]); setFamilie(false);
        setForm(p => ({ ...p, name: '', email: '', phone: '', notes: '', naechster_schritt: '' }));
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (typeof detail === 'string' ? detail : 'Anlegen fehlgeschlagen'));
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      const { kanal, ...rest } = form;
      await leadsAPI.create({
        ...rest,
        herkunft: { kanal },
        name: form.name.trim(),
        email: form.email?.trim().toLowerCase() || null,
        phone: form.phone ? form.phone.replace(/[\s\-\(\)]/g, '') : null,
      });
      toast.success('Mitglied erstellt');
      onSuccess();
      onClose();
      setForm({ name: '', email: '', phone: '', source: 'laufkundschaft', kanal: 'laufkundschaft', notes: '', stage: 'neuer_lead', assigned_to: '', bereich: '', altersgruppe: '', kampagne: '', naechster_schritt: '', termin_status: '', priority: 'mittel', custom_fields: {} });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join(', '));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erstellung fehlgeschlagen');
      }
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (field) => errors[field] ? <p className="text-[10px] text-destructive mt-0.5">{errors[field]}</p> : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle>{familie ? 'Familie mit Kindern anlegen' : 'Neues Mitglied hinzufügen'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="flex gap-1 text-xs flex-wrap" data-testid="lead-art">
            {[[false, 'Einzelperson'], [true, 'Familie mit Kindern']].map(([w, l]) => (
              <button key={l} type="button" onClick={() => setFamilie(w)} data-testid={w ? 'lead-art-familie' : 'lead-art-einzel'}
                className={`rounded-full px-3 h-8 border inline-flex items-center gap-1.5 ${familie === w ? 'bg-foreground text-background border-foreground' : 'border-border'}`}>
                {w ? <Users className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />} {l}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>{familie ? 'Name Elternteil *' : 'Name *'}</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} className={errors.name ? 'border-destructive' : ''} data-testid="lead-name-input" />
              {fieldError('name')}
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} className={errors.email ? 'border-destructive' : ''} data-testid="lead-email-input" />
              {fieldError('email')}
            </div>
            <div>
              <Label>{familie ? 'Handynummer der Eltern *' : 'Telefon'}</Label>
              <Input value={form.phone} onChange={(e) => setForm(p => ({...p, phone: e.target.value}))} placeholder="+49 170 1234567" className={errors.phone ? 'border-destructive' : ''} data-testid="lead-phone-input" />
              {fieldError('phone')}
            </div>

            {familie && (
              <div className="col-span-2 space-y-2 rounded-lg border border-border/60 p-3" data-testid="lead-kinder">
                <p className="text-xs text-muted-foreground">Jedes Kind wird ein eigener Lead mit der Nummer der Eltern. Nachname leer = Nachname des Elternteils.</p>
                {kinder.map((k, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
                    <div><Label className="text-xs">Kind {i + 1}: Vorname</Label><Input value={k.vorname} onChange={e => kindSetzen(i, 'vorname', e.target.value)} className="h-9" data-testid={`lead-kind-vorname-${i}`} /></div>
                    <div><Label className="text-xs">Nachname</Label><Input value={k.nachname} onChange={e => kindSetzen(i, 'nachname', e.target.value)} className="h-9" placeholder="wie Eltern" /></div>
                    <div><Label className="text-xs">Geburtsdatum</Label><Input type="date" value={k.geburtsdatum} onChange={e => kindSetzen(i, 'geburtsdatum', e.target.value)} className="h-9" /></div>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" disabled={kinder.length === 1} onClick={() => setKinder(ks => ks.filter((_, j) => j !== i))} title="Kind entfernen"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
                {kinder.length < 6 && <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setKinder(ks => [...ks, leerKind()])} data-testid="lead-kind-plus"><Plus className="w-3.5 h-3.5" /> weiteres Kind</Button>}
                {errors.kinder && <p className="text-[10px] text-destructive">{errors.kinder}</p>}
              </div>
            )}

            {/* Studio-spezifische Felder (Familie: Bereich Kinder & Jugend, Altersgruppe aus dem Geburtsdatum) */}
            {!familie && <>
            <div>
              <Label>Bereich</Label>
              <select value={form.bereich} onChange={(e) => setForm(p => ({...p, bereich: e.target.value}))} data-testid="lead-bereich" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                <option value="">– Auswählen –</option>
                {bereiche.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Altersgruppe</Label>
              <select value={form.altersgruppe} onChange={(e) => setForm(p => ({...p, altersgruppe: e.target.value}))} data-testid="lead-altersgruppe" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                <option value="">– Auswählen –</option>
                {altersgruppen.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </div>
            </>}
            <div>
              <Label>Quelle</Label>
              <select value={form.source} onChange={(e) => setForm(p => ({...p, source: e.target.value}))} data-testid="lead-source" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                {SOURCES.map(s => <option key={s} value={s}>{toTitleCase(s)}</option>)}
              </select>
            </div>
            <div>
              <Label>Kanal (Herkunft) *</Label>
              {/* Pflichtfeld (Baustein 1): Werte identisch zu herkunft.KANAELE
                  im Backend — daran haengt "Wert je Anfrage" je Kanal. */}
              <select value={form.kanal} onChange={(e) => setForm(p => ({...p, kanal: e.target.value}))} data-testid="lead-kanal" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                {['laufkundschaft', 'empfehlung', 'meta', 'google', 'instagram', 'whatsapp', 'flyer', 'direkt']
                  .map(k => <option key={k} value={k}>{toTitleCase(k)}</option>)}
              </select>
            </div>
            <div>
              <Label>Kampagne</Label>
              <select value={form.kampagne} onChange={(e) => setForm(p => ({...p, kampagne: e.target.value}))} data-testid="lead-kampagne" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                <option value="">Keine</option>
                {kampagnenList.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select value={form.stage} onChange={(e) => setForm(p => ({...p, stage: e.target.value}))} data-testid="lead-stage" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Priorität</Label>
              <select value={form.priority} onChange={(e) => setForm(p => ({...p, priority: e.target.value}))} data-testid="lead-priority" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                <option value="niedrig">Niedrig</option>
                <option value="mittel">Mittel</option>
                <option value="hoch">Hoch</option>
                <option value="dringend">Dringend</option>
              </select>
            </div>
            <div>
              <Label>Termin-Status</Label>
              <select value={form.termin_status} onChange={(e) => setForm(p => ({...p, termin_status: e.target.value}))} data-testid="lead-termin-status" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                <option value="">– Kein Termin –</option>
                <option value="geplant">Probetraining geplant</option>
                <option value="besucht">Probetraining besucht</option>
                <option value="no_show">No-Show</option>
                <option value="abgesagt">Abgesagt</option>
              </select>
            </div>
            {agents.length > 0 && (
              <div>
                <Label>Zuständig</Label>
                <select value={form.assigned_to} onChange={(e) => setForm(p => ({...p, assigned_to: e.target.value}))} data-testid="lead-assigned" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                  <option value="">Nicht zugewiesen</option>
                  {agents.map(a => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
                </select>
              </div>
            )}
            <div className="col-span-2">
              <Label>Nächster Schritt</Label>
              <Input value={schrittText(form.naechster_schritt)} onChange={(e) => setForm(p => ({...p, naechster_schritt: e.target.value}))} placeholder="z.B. Probetraining vereinbaren, Angebot senden" data-testid="lead-naechster-schritt" />
            </div>
            <div className="col-span-2">
              <Label>Notizen</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({...p, notes: e.target.value}))} rows={2} placeholder="Interesse, Vorkenntnisse, besondere Wünsche..." />
            </div>
            {customFieldDefs.length > 0 && (
              <>
                <div className="col-span-2 border-t pt-3 mt-1">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Eigene Felder</p>
                </div>
                {customFieldDefs.map((cf) => (
                  <div key={cf.key} className={cf.type === 'textarea' ? 'col-span-2' : ''}>
                    <Label className="text-xs">{cf.label} {cf.required && <span className="text-destructive">*</span>}</Label>
                    <CustomFieldInput field={cf} value={form.custom_fields?.[cf.key] || ''} onChange={(val) => setForm(p => ({...p, custom_fields: {...(p.custom_fields || {}), [cf.key]: val}}))} />
                  </div>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={saving} data-testid="save-lead-btn">{saving ? 'Wird gespeichert...' : 'Mitglied hinzufuegen'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}




function CustomFieldInput({ field, value, onChange }) {
  switch (field.type) {
    case 'select':
      return (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
{(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
</select>
      );
    case 'textarea':
      return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2} className="text-xs" placeholder={field.placeholder} required={field.required} />;
    case 'checkbox':
      return (
        <div className="flex items-center gap-2 pt-1">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="rounded border-input" />
          <span className="text-xs text-muted-foreground">{field.placeholder || 'Yes'}</span>
        </div>
      );
    case 'number':
      return <Input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-xs" placeholder={field.placeholder} required={field.required} />;
    case 'date':
      return <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-xs" required={field.required} />;
    default:
      return <Input type={field.type || 'text'} value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-xs" placeholder={field.placeholder} required={field.required} />;
  }
}


/* Massen-Archivierung: erst Vorschau (wie viele, welche Stufen), dann
   bewusst bestaetigen. Leads mit offener Wiedervorlage, kuenftigem Termin
   oder Abschluss nimmt das Backend grundsaetzlich aus. */
function ArchivAlteDialog({ open, onClose, onSuccess }) {
  const [tage, setTage] = useState(180);
  const [vorschau, setVorschau] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setVorschau(null); return; }
    let aktiv = true;
    const t = setTimeout(async () => {
      try {
        const res = await leadsAPI.archivAlte({ tage: Number(tage) || 0, vorschau: true });
        if (aktiv) setVorschau(res.data);
      } catch (err) {
        if (aktiv) setVorschau({ fehler: err.response?.data?.detail || 'Vorschau fehlgeschlagen' });
      }
    }, 400);
    return () => { aktiv = false; clearTimeout(t); };
  }, [open, tage]);

  const ausfuehren = async () => {
    setBusy(true);
    try {
      const res = await leadsAPI.archivAlte({ tage: Number(tage) });
      toast.success(`${res.data.archiviert} Lead(s) archiviert`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archivieren fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Alte Leads archivieren</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="archiv-tage">Keine Bewegung seit … Tagen (mind. 30)</Label>
            <Input id="archiv-tage" type="number" min={30} value={tage}
                   onChange={(e) => setTage(e.target.value)} data-testid="archiv-tage-input" />
          </div>
          <p className="text-xs text-muted-foreground">
            Ausgenommen sind immer: Kunden/Abschlüsse, Leads mit offener
            Wiedervorlage und Leads mit Termin oder nächstem Schritt in der
            Zukunft. Archivierte Leads werden nicht gelöscht und kommen bei
            neuer Aktivität automatisch zurück.
          </p>
          {vorschau?.fehler && <p className="text-sm text-destructive">{vorschau.fehler}</p>}
          {vorschau && !vorschau.fehler && (
            <div className="rounded-md border p-3 text-sm space-y-1" data-testid="archiv-vorschau">
              <p><b>{vorschau.anzahl}</b> Lead(s) würden archiviert.</p>
              {(vorschau.je_stage || []).map((s) => (
                <p key={s.stage} className="text-xs text-muted-foreground">
                  {toTitleCase(s.stage || 'ohne Stufe')}: {s.anzahl}
                </p>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={ausfuehren} data-testid="archiv-alte-ausfuehren"
                  disabled={busy || !vorschau || vorschau.fehler || !vorschau.anzahl}>
            {busy ? 'Archiviere …' : `${vorschau?.anzahl ?? 0} Lead(s) archivieren`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ActionsMenu({ leadId, onView, onDelete, archiviert, onArchiv }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 140 });
    }
    setOpen(!open);
  };

  return (
    <div className="relative">
      <Button
        ref={btnRef}
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        data-testid={`lead-actions-${leadId}`}
        onClick={handleOpen}
      >
        <MoreHorizontal className="w-4 h-4" />
      </Button>
      {open && ReactDOM.createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 min-w-[140px] bg-background border border-border rounded-md shadow-lg py-1"
            style={{ top: pos.top, left: pos.left }}
          >
            <button
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors"
              onClick={() => { setOpen(false); onView(); }}
              data-testid={`action-view-${leadId}`}
            >
              Details anzeigen
            </button>
            {onArchiv && (
              <button
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors"
                onClick={() => { setOpen(false); onArchiv(); }}
                data-testid={`action-archiv-${leadId}`}
              >
                {archiviert ? 'Aus dem Archiv holen' : 'Archivieren'}
              </button>
            )}
            {onDelete && (
              <button
                className="w-full text-left px-3 py-1.5 text-sm text-destructive hover:bg-muted transition-colors"
                onClick={() => { setOpen(false); onDelete(); }}
                data-testid={`action-delete-${leadId}`}
              >
                Loeschen
              </button>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
